import { useEffect, useRef, useState } from "react";
import { useAiTrace } from "../api/hooks/useAiTrace";
import { hasAITrace } from "../api/models/aiTrace";
import { AiTraceViewer } from "./AiTraceViewer";
import { DemoBlur } from "./DemoBlur";
import styles from "./ViewAiTraceButton.module.css";

interface Props {
  // (surface, artefactId) address the persisted trace. surface must be one of
  // the backend's ai_trace_store.SURFACE_LABELS registry. Nullable because some cost
  // sources (gmail_sync / admin_health / unattributed) carry no trace artefact -
  // those rows pass null + hasTrace=false, so the button renders nothing (the
  // `!hasTrace` guard short-circuits before surface/id are ever used).
  surface: string | null;
  artefactId: string | null;
  label?: string;
  // Whether a trace actually exists for this artefact. The button renders
  // nothing when this is falsy, so artefacts from before trace capture (their
  // backend has_ai_trace flag is null) don't offer a dead "View AI trace"
  // button. This component is the single normalisation point for the BE's
  // three-state flag: call sites pass the persisted value RAW (true / false /
  // null / undefined) and `!hasTrace` collapses every absent case to "hidden",
  // so no caller needs its own Boolean() coercion.
  hasTrace?: boolean | null;
  // Optional per-model token share of the run (sorted DESC), rendered as a
  // totals line at the top of the modal so the operator sees the model split
  // ("75% opus-4-8 · 25% haiku-4-5") before the raw interaction. Only the
  // /costs surface passes this; other surfaces omit it.
  modelUsage?: { model_id: string | null; tokens: number; pct: number }[] | null;
}

// Compact a Bedrock profile id: "eu.anthropic.claude-opus-4-8-v1" -> "opus-4-8".
const _shortModel = (m: string | null): string =>
  (m ?? "unknown")
    .replace(/^eu\.anthropic\./, "")
    .replace(/^anthropic\./, "")
    .replace(/^claude-/, "")
    .replace(/-v\d+$/, "");

// Drop-in button that opens a modal showing the complete AI interaction trace
// for one artefact (CLAUDE.md principle 12). Self-contained: it owns the modal,
// fires useAiTrace only once opened (the hook is gated on a non-null surface +
// id), and handles loading / empty / error states. The two later lanes drop
// this onto Run / Procurement / Extraction / Global-data surfaces with just a
// (surface, artefactId) pair.
export const ViewAiTraceButton = ({
  surface,
  artefactId,
  label = "View AI trace",
  hasTrace,
  modelUsage,
}: Props) => {
  const ref = useRef<HTMLDialogElement | null>(null);
  const [open, setOpen] = useState(false);

  // Only fetch once the modal is open - traces are large and admin-only, so
  // there's no reason to pull one for every row that renders a button.
  const {
    data: trace,
    isLoading,
    isError,
  } = useAiTrace(
    open ? (surface ?? undefined) : undefined,
    open ? (artefactId ?? undefined) : undefined,
  );

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  // No trace for this artefact (e.g. a run from before trace capture, or a
  // pure-code extraction) -> offer nothing. After the hooks so React's
  // rules-of-hooks hold.
  if (!hasTrace) return null;

  return (
    <>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen(true)}
      >
        {label}
      </button>
      <dialog
        ref={ref}
        className={styles.dialog}
        onCancel={(e) => {
          e.preventDefault();
          setOpen(false);
        }}
      >
        <div className={styles.body}>
          <div className={styles.headerRow}>
            <h2 className={styles.heading}>AI interaction trace</h2>
            <button
              type="button"
              className={styles.btnClose}
              onClick={() => setOpen(false)}
            >
              Close
            </button>
          </div>

          {modelUsage && modelUsage.length > 0 ? (
            <p className={styles.modelTotals}>
              {modelUsage.length === 1
                ? `Model: ${_shortModel(modelUsage[0].model_id)}`
                : `Models: ${modelUsage
                    .map((m) => `${m.pct}% ${_shortModel(m.model_id)}`)
                    .join(" · ")}`}
            </p>
          ) : null}

          {isLoading ? (
            <p className={styles.status}>Loading trace...</p>
          ) : isError ? (
            <div className={styles.error}>
              Could not load the AI trace. It may not have been captured, or the
              trace store is unavailable in this environment.
            </div>
          ) : hasAITrace(trace) ? (
            // The trace is the raw model I/O (prompts, file contents, tool
            // results) - blur its content in demo mode while keeping the
            // viewer structure visible.
            <DemoBlur>
              <AiTraceViewer trace={trace} />
            </DemoBlur>
          ) : (
            <p className={styles.status}>No trace available.</p>
          )}

          <div className={styles.actions}>
            <button
              type="button"
              className={styles.btnClose}
              onClick={() => setOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      </dialog>
    </>
  );
};

export default ViewAiTraceButton;
